import React, { useState, useMemo } from 'react';
import TicketItem, { TicketHolder } from './TicketItem';
import { getLoteAtivo, precoDoIngresso } from '../config/event';

interface RegistrationFormProps {
  eventPrice: number;
  onTicketCountChange: (count: number) => void;
  onFormSubmit: (formData: TicketHolder[]) => void;
  isLoading: boolean;
  onAddTicket: (updatedFormData: TicketHolder[]) => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onTicketCountChange,
  onFormSubmit,
  isLoading,
  onAddTicket,
}) => {
  const [ticketHolders, setTicketHolders] = useState<TicketHolder[]>([
    { name: '', documentNumber: '', email: '', cellNumber: '', ticketType: 'Inteira', comprovanteNumber: '' }
  ]);
  const [error, setError] = useState<string | null>(null);

  const lote = useMemo(() => getLoteAtivo(), []);

  const { countInteira, countMeia, valorTotal } = useMemo(() => {
    let inteiras = 0;
    let meias = 0;
    let total = 0;
    for (const t of ticketHolders) {
      if (t.ticketType === 'Meia') {
        meias += 1;
        total += precoDoIngresso('Meia', lote);
      } else {
        inteiras += 1;
        total += precoDoIngresso('Inteira', lote);
      }
    }
    return { countInteira: inteiras, countMeia: meias, valorTotal: total };
  }, [ticketHolders, lote]);

  const handleAddTicket = () => {
    const newTicketHolders: TicketHolder[] = [
      ...ticketHolders,
      { name: '', documentNumber: '', email: '', cellNumber: '', ticketType: 'Inteira', comprovanteNumber: '' }
    ];
    setTicketHolders(newTicketHolders);
    onTicketCountChange(newTicketHolders.length);
    onAddTicket(newTicketHolders);
  };

  const handleTicketHolderChange = (index: number, field: keyof TicketHolder, value: string) => {
    const newTicketHolders = [...ticketHolders];
    if (field === 'ticketType') {
      newTicketHolders[index][field] = value as 'Meia' | 'Inteira';
    } else {
      newTicketHolders[index][field] = value;
    }
    setTicketHolders(newTicketHolders);
  };

  const handleRemoveTicket = (index: number) => {
    const newTicketHolders = ticketHolders.filter((_, i) => i !== index);
    setTicketHolders(newTicketHolders);
    onTicketCountChange(newTicketHolders.length);
    onAddTicket(newTicketHolders);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    for (let i = 0; i < ticketHolders.length; i++) {
      const ticketHolder = ticketHolders[i];
      if (i === 0) {
        if (!ticketHolder.name.trim() || !ticketHolder.documentNumber.trim() || !ticketHolder.email?.trim() || !ticketHolder.cellNumber?.trim()) {
          setError('Por favor, preencha todos os campos obrigatórios do primeiro ingresso (comprador titular).');
          return;
        }
      } else {
        if (!ticketHolder.name.trim() || !ticketHolder.documentNumber.trim()) {
          setError(`Por favor, preencha nome e documento para o ingresso ${i + 1}.`);
          return;
        }
      }
    }
    setError(null);
    onFormSubmit(ticketHolders);
  };

  const formatBRL = (v: number) => `R$\u00A0${v.toFixed(2).replace('.', ',')}`;

  return (
    <div className="registration-flow-card">
      <div className="registration-flow-header">
        <div className="flow-step-tag">Etapa 1 de 2</div>
        <h3 className="registration-title">Titularidade dos Ingressos</h3>
        <p className="registration-subtitle">
          Informe os dados dos participantes. Os ingressos com QR Code serão emitidos e enviados para o e-mail do titular.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="registration-form-element">
        <div className="ticket-items-list">
          {ticketHolders.map((ticketHolder, index) => (
            <TicketItem
              key={index}
              index={index}
              ticketHolder={ticketHolder}
              onChange={handleTicketHolderChange}
              onAddTicket={() => onAddTicket(ticketHolders)}
              onRemoveTicket={handleRemoveTicket}
              hideTicketType={false}
            />
          ))}
        </div>

        {error && (
          <div className="form-error-alert" role="alert">
            <span><strong>Atenção — </strong>{error}</span>
          </div>
        )}

        {/* Resumo do Pedido em tempo real */}
        <div className="order-summary-box">
          <div className="summary-title">Resumo do Pedido ({lote.nome})</div>
          <div className="summary-breakdown">
            {countInteira > 0 && (
              <div className="summary-row">
                <span>{countInteira}x Ingresso Inteira ({formatBRL(lote.inteira)})</span>
                <span>{formatBRL(countInteira * lote.inteira)}</span>
              </div>
            )}
            {countMeia > 0 && (
              <div className="summary-row">
                <span>{countMeia}x Meia-entrada ({formatBRL(lote.meia)})</span>
                <span>{formatBRL(countMeia * lote.meia)}</span>
              </div>
            )}
            <div className="summary-divider" />
            <div className="summary-row summary-total">
              <span>Total a Pagar</span>
              <span className="total-amount">{formatBRL(valorTotal)}</span>
            </div>
          </div>
        </div>

        <div className="registration-actions">
          <button
            type="button"
            onClick={handleAddTicket}
            className="btn-era-outline"
            disabled={isLoading}
          >
            + Adicionar outro ingresso
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="btn-era-primary"
          >
            {isLoading ? (
              <span className="btn-loading-state">
                <span className="spinner-era" /> Processando...
              </span>
            ) : (
              `Ir para Pagamento • ${formatBRL(valorTotal)}`
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;